/**
 * OptionsMenu — ปุ่มเปิด popup ของคำสั่งที่เป็นเรื่อง "ตัวคน" ไม่ใช่เรื่องของงาน
 * (เปลี่ยนรหัสผ่าน, ออกจากระบบ) แทนที่ปุ่มแยกบน header (`WO-P2-M11-004`, D-107 ข้อ 7)
 *
 * **ไม่ใช่ dialog แบบ `Modal`** — เจตนาต่างกัน: popup นี้ต้องปิดได้จาก Escape,
 * คลิกนอกกล่อง หรือกดรายการข้างใน แล้วคืนโฟกัสกลับปุ่มเปิด แต่ **ห้ามดักโฟกัสไว้
 * ข้างใน** ผู้ใช้ต้อง Tab ผ่านออกไปยังส่วนที่เหลือของหน้าได้ตามปกติ — กล่องที่ดัก
 * โฟกัสไว้แย่กว่าลิงก์แยกที่มันมาแทนที่ (ตามที่ผู้ใช้ระบุไว้ตอนสั่งงานนี้)
 *
 * ปุ่มเปิดต้อง render และกดได้เสมอ ไม่มีเงื่อนไขสิทธิ์ใด ๆ — ดูเหตุผลที่
 * `HeaderBar.tsx` เขียนไว้ตรงจุดที่เรียกใช้
 */

import { useEffect, useId, useRef, useState, type ReactNode } from 'react';

export interface OptionsMenuProps {
  /** ทั้งข้อความบนปุ่มเปิดและชื่อที่ screen reader ใช้เรียก popup */
  readonly triggerLabel: string;
  /** รายการข้างใน — ลิงก์/ปุ่มจริงที่มี label ของตัวเองอยู่แล้ว ไม่ใช่ `role="menuitem"` */
  readonly children: ReactNode;
}

export function OptionsMenu({ triggerLabel, children }: OptionsMenuProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  /** true เฉพาะช่วงที่เพิ่งปิดจาก "เปิดอยู่" — กันไม่ให้แย่งโฟกัสตอนเมานต์ครั้งแรก */
  const wasOpenRef = useRef(false);
  const popupId = useId();

  useEffect(() => {
    if (open) {
      wasOpenRef.current = true;
      return;
    }
    if (wasOpenRef.current) {
      wasOpenRef.current = false;
      triggerRef.current?.focus();
    }
  }, [open]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const handlePointerDown = (event: MouseEvent) => {
      if (rootRef.current !== null && !rootRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        // กันไม่ให้ Esc ทะลุไปสั่งอย่างอื่นบนหน้าจอที่อยู่หลัง popup
        event.stopPropagation();
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  return (
    <div className="cap-options" ref={rootRef}>
      <button
        type="button"
        ref={triggerRef}
        className="cap-button cap-button--ghost"
        data-testid="header-options-trigger"
        aria-haspopup="true"
        aria-expanded={open}
        aria-controls={popupId}
        onClick={() => {
          setOpen((current) => !current);
        }}
      >
        {triggerLabel}
        <span aria-hidden="true" className="cap-options__caret">
          {open ? '▴' : '▾'}
        </span>
      </button>
      {open ? (
        <div
          id={popupId}
          className="cap-options__popup"
          aria-label={triggerLabel}
          data-testid="header-options-popup"
          // กดรายการข้างใน (ลิงก์/ปุ่ม) แล้วปิด popup ทันที — delegate ที่กล่อง
          // แทนที่จะสั่งให้ children แต่ละตัวรู้จัก popup ที่ห่อตัวเองอยู่
          onClick={(event) => {
            if ((event.target as HTMLElement).closest('a, button') !== null) {
              setOpen(false);
            }
          }}
        >
          {children}
        </div>
      ) : null}
    </div>
  );
}
